import type { QuotationMetatadaTableDto } from '@api/machines'
import {
  APPROVAL_STATUSES,
  type BulkActionsSource,
  QuoteStatus,
  type SelectedEquipment
} from '@common/utils'
import { MolsonBox, MolsonIcon, MolsonTypography } from '@components/reusable'
import type { SxProps } from '@mui/material'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'
import { BulkActionsSources } from '../constants'
import { BulkActionsContainer } from '../styled-components/styles'

export const GridBulkActions = ({
  keyPrefix,
  selectedItems,
  source,
  onArchiveItems,
  onRestoreItems,
  onDeleteItems,
  customPositionSx
}: {
  keyPrefix: string
  selectedItems: QuotationMetatadaTableDto[] | SelectedEquipment[]
  source: BulkActionsSource
  onArchiveItems: () => void
  onRestoreItems: () => void
  onDeleteItems?: () => void
  customPositionSx?: SxProps
}) => {
  const quotesActions = source === BulkActionsSources.QUOTES
  const equipmentsActions = source === BulkActionsSources.EQUIPMENTS

  const theme = molsonUseTheme()

  const { t } = useTranslation('', {
    keyPrefix: keyPrefix
  })

  let archivedItems: SelectedEquipment[] | QuotationMetatadaTableDto[] = []
  let activeItems: SelectedEquipment[] | QuotationMetatadaTableDto[] = []
  let deletableItems: QuotationMetatadaTableDto[] = []

  if (equipmentsActions) {
    const selectedEquipments = selectedItems as SelectedEquipment[]

    archivedItems = selectedEquipments.filter(item => item.isArchived)
    activeItems = selectedEquipments.filter(item => !item.isArchived)
  } else if (quotesActions) {
    const quoteItems = selectedItems as QuotationMetatadaTableDto[]

    archivedItems = quoteItems.filter(
      item => item.status === QuoteStatus.ARCHIVED
    )

    activeItems = quoteItems.filter(
      item =>
        item.status !== QuoteStatus.ARCHIVED &&
        !APPROVAL_STATUSES.includes(item.status as QuoteStatus)
    )

    deletableItems = quoteItems.filter(
      item => !APPROVAL_STATUSES.includes(item.status as QuoteStatus)
    )
  }

  const getButtons = () => {
    if (equipmentsActions) {
      return (
        <>
          {archivedItems.length > 0 && (
            <BulkActionsContainer onClick={onRestoreItems}>
              <MolsonIcon variant="RESTORE" />
              <MolsonTypography variant="h4">
                {t('restoreQuoteItems', {
                  nrOfItems: archivedItems.length
                })}
              </MolsonTypography>
            </BulkActionsContainer>
          )}
          {activeItems.length > 0 && (
            <BulkActionsContainer onClick={onArchiveItems}>
              <MolsonIcon variant="ARCHIVE_FILLED" iconColor="error" />
              <MolsonTypography
                variant="h4"
                color={theme.palette.primary.error}
              >
                {t('archiveQuoteItems', {
                  nrOfItems: activeItems.length
                })}
              </MolsonTypography>
            </BulkActionsContainer>
          )}
        </>
      )
    } else if (quotesActions) {
      return (
        <>
          {archivedItems.length > 0 && (
            <BulkActionsContainer onClick={onRestoreItems}>
              <MolsonIcon variant="RESTORE" />
              <MolsonTypography variant="h4">
                {t('restoreQuoteItems', {
                  nrOfItems: archivedItems.length
                })}
              </MolsonTypography>
            </BulkActionsContainer>
          )}

          {activeItems.length > 0 && (
            <BulkActionsContainer onClick={onArchiveItems}>
              <MolsonIcon variant="ARCHIVE_FILLED" />
              <MolsonTypography variant="h4">
                {t('archiveQuoteItems', {
                  nrOfItems: activeItems.length
                })}
              </MolsonTypography>
            </BulkActionsContainer>
          )}
          {deletableItems.length > 0 && (
            <BulkActionsContainer onClick={onDeleteItems}>
              <MolsonIcon variant="TRASH_CONTAINED" iconColor="error" />
              <MolsonTypography
                variant="h4"
                color={theme.palette.primary.error}
              >
                {t('deleteQuoteItems', {
                  nrOfItems: deletableItems.length
                })}
              </MolsonTypography>
            </BulkActionsContainer>
          )}
        </>
      )
    }
  }

  return (
    <MolsonBox
      sx={{
        position: 'absolute',
        right: 320,
        top: 10,
        zIndex: 2,
        ...customPositionSx
      }}
    >
      <MolsonBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {((selectedItems.length === 0 && equipmentsActions) ||
          quotesActions) && (
          <MolsonTypography variant="h4" color={theme.palette.secondary[600]}>
            {t('selectedQuoteItems', {
              nrOfItems: selectedItems.length
            })}
          </MolsonTypography>
        )}

        {getButtons()}
      </MolsonBox>
    </MolsonBox>
  )
}
